/**
 * Compose "abort when the caller aborts OR after `ms`" without depending on
 * `AbortSignal.any`, which needs Chrome 116 / Firefox 124 / Safari 17.4. On an
 * older engine the bare call throws synchronously *inside the queryFn* — React
 * Query retries the throw and settles in isError, and because useUser feeds
 * the Navbar, dashboard and checkout redirect, every visitor on that browser
 * rendered as errored/unauthenticated. There is no polyfill anywhere in the
 * app, so the fallback lives here, next to the one place that needs it.
 *
 * Both branches yield the same observable contract: the returned signal's
 * `reason` is the parent's reason when the parent aborts, and a DOMException
 * named "TimeoutError" when the timer fires — the name classifyFetchFailure
 * keys on, so log severity does not change between engines.
 */
export function withTimeout(parent: AbortSignal | undefined, ms: number): AbortSignal {
  const Signal = AbortSignal as typeof AbortSignal & {
    any?: (signals: AbortSignal[]) => AbortSignal
    timeout?: (ms: number) => AbortSignal
  }
  if (typeof Signal.any === "function" && typeof Signal.timeout === "function") {
    return parent ? Signal.any([parent, Signal.timeout(ms)]) : Signal.timeout(ms)
  }

  const controller = new AbortController()
  if (parent?.aborted) {
    controller.abort(parent.reason)
    return controller.signal
  }

  let timer: ReturnType<typeof setTimeout> | null = null
  const onParentAbort = () => {
    if (timer !== null) clearTimeout(timer)
    timer = null
    controller.abort(parent?.reason)
  }
  timer = setTimeout(() => {
    timer = null
    parent?.removeEventListener("abort", onParentAbort)
    controller.abort(new DOMException("The operation timed out.", "TimeoutError"))
  }, ms)
  // { once } is not enough on its own: the timeout path removes the listener
  // explicitly so a long-lived parent (React Query's per-observer signal) does
  // not accumulate one dead closure per request.
  parent?.addEventListener("abort", onParentAbort, { once: true })
  return controller.signal
}
